import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import "./EmailNotification.css";
import api from "../../services/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import FullscreenModal from "../../components/common/FullscreenModal/FullscreenModal";
import { formatAdminDate, formatAdminDateTime, formatIST } from "../../utils/datetime";

// ── SVG Icons ──────────────────────────────────────────────
const SendIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const StopIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <rect x="9" y="9" width="6" height="6" fill="currentColor"></rect>
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4h6v2" />
  </svg>
);

const OpenTrackIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
  </svg>
);

// ── Helper: human-readable file size ───────────────────────
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

// ── Helper: file icon by extension ─────────────────────────
const getFileIcon = (name) => {
  const ext = name.split(".").pop().toLowerCase();
  if (["pdf"].includes(ext)) return "📄";
  if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)) return "🖼️";
  if (["doc", "docx"].includes(ext)) return "📝";
  if (["xls", "xlsx", "csv"].includes(ext)) return "📊";
  if (["zip", "rar", "7z"].includes(ext)) return "📦";
  return "📎";
};

// ── Helper: robust CC email parsing ─────────────────────────
const parseCcEmails = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === 'string') {
    if (!data.trim()) return [];
    try {
      const parsed = JSON.parse(data);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

/* ============================================================
   COMPOSE FORM  (rendered inside FullscreenModal)
   ============================================================ */
const ComposeEmailForm = forwardRef(({ onSubmitted, onCancel, initialData }, ref) => {
  const [subject, setSubject] = useState(initialData?.subject || "");
  const [htmlBody, setHtmlBody] = useState(initialData?.html_body || "");
  const [recipientQuery, setRecipientQuery] = useState(initialData?.recipient_query_id || "");
  const [notificationType, setNotificationType] = useState(initialData?.notification_type || "general");
  const [ccEmails, setCcEmails] = useState(parseCcEmails(initialData?.cc_emails));
  const [ccInput, setCcInput] = useState("");
  const [archiveBcc, setArchiveBcc] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);

  // Dropdown data
  const [queryTypes, setQueryTypes] = useState([]);
  const fileInputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    handleCloseAttempt: async () => {
      const hasContent = subject.trim() || (htmlBody.trim() && htmlBody !== "<p><br></p>");
      if (hasContent) {
        // Submit as draft silently
        await handleSubmit(null, true);
      }
    }
  }));

  useEffect(() => {
    api.get("/notification-query")
      .then((res) => setQueryTypes(res.data || []))
      .catch((err) => console.error("Error fetching query types:", err));

    api.get("/email-notifications/config/archive-email")
      .then((res) => {
        if (res?.data?.archive_email) {
          setArchiveBcc(res.data.archive_email);
        }
      })
      .catch((err) => console.error("Error fetching archive email config:", err));
  }, []);

  // ── CC helpers ──────────────────────────────────────────
  const addCcEmail = (raw) => {
    const email = raw.trim().toLowerCase();
    if (!email) return;
    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Invalid email address");
      return;
    }
    if (ccEmails.includes(email)) {
      toast.warn("Email already added");
      return;
    }
    setCcEmails((prev) => [...prev, email]);
    setCcInput("");
  };

  const removeCcEmail = (email) => {
    setCcEmails((prev) => prev.filter((e) => e !== email));
  };

  const handleCcKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addCcEmail(ccInput);
    }
    if (e.key === "Backspace" && !ccInput && ccEmails.length > 0) {
      setCcEmails((prev) => prev.slice(0, -1));
    }
  };

  // ── Attachment helpers ─────────────────────────────────
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    let hasError = false;

    for (let file of files) {
      if (file.size > 5 * 1024 * 1024) {
        hasError = true;
      } else {
        validFiles.push(file);
      }
    }

    if (hasError) {
      toast.error("One or more files exceed the 5MB limit and were removed.");
    }

    setAttachments((prev) => [...prev, ...validFiles]);
    e.target.value = "";
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Submit ─────────────────────────────────────────────
  const handleSubmit = async (e, isDraft = false) => {
    if (e) e.preventDefault();
    if (!isDraft) {
      if (!subject.trim()) { toast.error("Subject is required"); return; }
      if (!recipientQuery) { toast.error("Please select recipients"); return; }
      if (!htmlBody.trim() || htmlBody === "<p><br></p>") { toast.error("Email body is required"); return; }
    }

    try {
      setSending(true);

      const formData = new FormData();
      formData.append("subject", subject);
      formData.append("html_body", htmlBody);
      if (recipientQuery) formData.append("recipient_query", recipientQuery);
      formData.append("notification_type", notificationType);
      formData.append("cc_emails", JSON.stringify(ccEmails));
      if (isDraft) formData.append("is_draft", true);
      if (initialData?.id) formData.append("draft_id", initialData.id);

      attachments.forEach((file) => {
        formData.append("attachments", file);
      });

      await api.post("/email-notifications/send", formData);
      toast.success(isDraft ? "Draft saved successfully!" : "✅ Email sent successfully!");
      if (onSubmitted) onSubmitted();
    } catch (error) {
      const msg = error?.message || "Failed to send email";
      toast.error("❌ " + msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ position: "relative" }}>
      {sending && <div className="email-loading-overlay">Sending email…</div>}

      <form className="email-compose-form" onSubmit={handleSubmit}>

        <div className="form-row">
          {/* Recipients (Query Type) */}
          <div className="form-group">
            <label>To (Recipients) <span style={{ color: "var(--color-danger)" }}>*</span></label>
            <select
              value={recipientQuery}
              onChange={(e) => setRecipientQuery(e.target.value)}
              required
            >
              <option value="" disabled>Select recipient group</option>
              {queryTypes.map((qt) => (
                <option key={qt.id} value={qt.id}>{qt.query_title}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Notification Type</label>
            <select value={notificationType} onChange={(e) => setNotificationType(e.target.value)}>
              <option value="general">General</option>
              <option value="marketing">Marketing</option>
              <option value="transactional">Transactional</option>
              <option value="announcement">Announcement</option>
              <option value="alert">Alert</option>
            </select>
          </div>
        </div>

        {/* CC Emails */}
        <div className="form-group full-width">
          <label>CC</label>
          <div className="cc-input-wrapper" onClick={() => document.getElementById("cc-email-input")?.focus()}>
            {ccEmails.map((email) => (
              <span className="cc-chip" key={email}>
                {email}
                <button type="button" onClick={() => removeCcEmail(email)}>×</button>
              </span>
            ))}
            <input
              id="cc-email-input"
              className="cc-input"
              type="email"
              value={ccInput}
              onChange={(e) => setCcInput(e.target.value.replace(",", ""))}
              onKeyDown={handleCcKeyDown}
              onBlur={() => { if (ccInput.trim()) addCcEmail(ccInput); }}
              placeholder={ccEmails.length === 0 ? "Type email and press Enter" : ""}
            />
          </div>
          <span className="cc-hint">Press Enter or comma to add. Backspace to remove last.</span>
        </div>

        {/* BCC Emails (Read-Only) */}
        {archiveBcc && (
          <div className="form-group full-width">
            <label>BCC</label>
            <div className="cc-input-wrapper disabled" style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}>
              <span className="cc-chip" style={{ backgroundColor: '#e0e0e0', color: '#666', border: '1px solid #ccc' }}>
                {archiveBcc}
              </span>
            </div>
            <span className="cc-hint">All outgoing emails are automatically archived to this address.</span>
          </div>
        )}

        {/* Subject */}
        <div className="form-group full-width">
          <label>Subject <span style={{ color: "var(--color-danger)" }}>*</span></label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Enter email subject"
            required
          />
        </div>

        {/* HTML Body */}
        <div className="form-group full-width quill-editor-group">
          <label>Email Body <span style={{ color: "var(--color-danger)" }}>*</span></label>
          <ReactQuill
            theme="snow"
            value={htmlBody}
            onChange={setHtmlBody}
            placeholder="Compose your email content here…"
            modules={{
              toolbar: [
                [{ header: [1, 2, 3, false] }],
                ["bold", "italic", "underline", "strike", "blockquote"],
                [{ list: "ordered" }, { list: "bullet" }],
                ["link", "image"],
                [{ color: [] }, { background: [] }],
                [{ align: [] }],
                ["clean"],
              ],
            }}
          />
        </div>

        {/* Attachments */}
        <div className="form-group full-width">
          <label>Attachments</label>
          <div
            className="attachment-zone"
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="attachment-zone-content">
              <span className="upload-icon">📎</span>
              <span>Click to add attachments</span>
              <small>PDF, Images, Documents, Spreadsheets etc.</small>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />
          </div>

          {attachments.length > 0 && (
            <div className="attachment-list">
              {attachments.map((file, idx) => (
                <div className="attachment-item" key={idx}>
                  <span className="attachment-icon">{getFileIcon(file.name)}</span>
                  <div className="attachment-info">
                    <div className="attachment-name">{file.name}</div>
                    <div className="attachment-size">{formatFileSize(file.size)}</div>
                  </div>
                  <button
                    type="button"
                    className="attachment-remove"
                    onClick={() => removeAttachment(idx)}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="email-compose-actions">
          <button type="button" className="email-cancel-btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="email-draft-btn" disabled={sending} onClick={() => handleSubmit(null, true)}>
            Save Draft
          </button>
          <button type="submit" className="email-send-btn" disabled={sending}>
            <SendIcon /> Send Email
          </button>
        </div>
      </form>
    </div>
  );
});

/* ============================================================
   MAIN PAGE — sent-emails table + compose modal
   ============================================================ */
const EmailNotification = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null);
  const [viewingEmail, setViewingEmail] = useState(null);
  const composeRef = useRef(null);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [filterType, setFilterType] = useState("");
  const [notificationTypes, setNotificationTypes] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(0);
  const activeRequestId = useRef(0);
  const limit = 20;

  // Email open tracking modal state
  const [opensModal, setOpensModal] = useState(null);
  const [opensData, setOpensData] = useState(null);
  const [opensLoading, setOpensLoading] = useState(false);
  const [opensPage, setOpensPage] = useState(1);

  useEffect(() => {
    api.get("/email-notifications/types")
      .then((res) => setNotificationTypes(res.data || []))
      .catch((err) => console.error("Error fetching notification types:", err));
  }, []);

  const fetchEmails = async () => {
    const requestId = ++activeRequestId.current;
    setLoading(true);
    try {
      const params = { limit, page };
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (filterType) params.type = filterType;
      if (activeSearch) params.search = activeSearch;

      const response = await api.get("/email-notifications", { params });
      if (requestId !== activeRequestId.current) return;
      setEmails(response.data || []);
      setTotalPages(response.pagination?.totalPages || 1);
    } catch (error) {
      if (requestId !== activeRequestId.current) return;
      console.error("Error fetching email notifications:", error);
    } finally {
      if (requestId === activeRequestId.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchEmails();
  }, [page, activeSearch, searchTrigger]);

  // Auto-refresh if any email is currently 'sending' or 'pending'
  useEffect(() => {
    const hasActiveEmails = emails.some((email) => email.status === 'sending' || email.status === 'pending');
    let intervalId;

    if (hasActiveEmails) {
      intervalId = setInterval(() => {
        // Fetch quietly without triggering the main loading spinner
        const params = { limit, page };
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;
        if (filterType) params.type = filterType;
        if (activeSearch) params.search = activeSearch;
        const requestId = ++activeRequestId.current;
        api.get("/email-notifications", { params }).then((res) => {
          if (requestId !== activeRequestId.current) return;
          setEmails(res.data || []);
          setTotalPages(res.pagination?.totalPages || 1);
        }).catch(err => {
          if (requestId !== activeRequestId.current) return;
          console.error("Auto-refresh failed", err);
        });
      }, 3000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [emails, page, fromDate, toDate, filterType, activeSearch]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this email record?")) return;
    try {
      await api.delete(`/email-notifications/${id}`);
      toast.success("Email record deleted");
      fetchEmails();
    } catch (error) {
      toast.error(error?.message || "Failed to delete");
    }
  };

  const handleResend = async (id) => {
    if (!window.confirm("Resend this email?")) return;
    try {
      await api.post(`/email-notifications/${id}/resend`);
      toast.success("Email resent successfully! Waiting 10 seconds...");
      fetchEmails();
    } catch (error) {
      toast.error(error?.message || "Failed to resend");
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Stop sending this email broadcast?")) return;
    try {
      await api.post(`/email-notifications/${id}/cancel`);
      toast.success("Email broadcast stopped!");
      fetchEmails();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to cancel");
    }
  };

  const handleModalClose = async () => {
    if (composeRef.current) {
      await composeRef.current.handleCloseAttempt();
    }
    setIsComposeOpen(false);
    setEditingDraft(null);
    fetchEmails(); // Refresh list to show new draft if saved
  };

  // Fetch email open events for a specific email notification
  const fetchOpenEvents = async (emailId, pageNum = 1) => {
    setOpensLoading(true);
    try {
      const response = await api.get(`/email-notifications/${emailId}/opens`, { params: { page: pageNum, limit: 20 } });
      setOpensData(response.data || null);
    } catch (error) {
      console.error("Error fetching open events:", error);
      toast.error("Failed to fetch open tracking data");
    } finally {
      setOpensLoading(false);
    }
  };

  const handleViewOpens = (row) => {
    setOpensModal(row);
    setOpensPage(1);
    fetchOpenEvents(row.id, 1);
  };

  const handleOpensPageChange = (newPage) => {
    setOpensPage(newPage);
    if (opensModal) fetchOpenEvents(opensModal.id, newPage);
  };

  return (
    <div className="email-notif-container">
      <ToastContainer position="bottom-right" autoClose={2000} />

      {/* Header */}
      <div className="email-notif-header">
        <div className="email-notif-header-left">
          <h1>Email Notifications</h1>
          <p>Compose and manage email broadcasts</p>
        </div>
        <button
          className="email-notif-compose-btn"
          onClick={() => { setEditingDraft(null); setIsComposeOpen(true); }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>edit</span>
          Compose Email
        </button>
      </div>

      {/* Filters */}
      <div className="email-notif-filters">
        <div className="email-notif-filter-group">
          <label>From</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div className="email-notif-filter-group">
          <label>To</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        <div className="email-notif-filter-group">
          <label>Type</label>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="">All Types</option>
            {notificationTypes.map((t) => (
              <option key={t} value={t}>
                {t.charAt(0).toUpperCase() + t.slice(1).replace(/_/g, " ")}
              </option>
            ))}
          </select>
        </div>
        <div className="email-notif-filter-group email-notif-search-group">
          <label>Search</label>
          <div className="email-notif-search-input-wrap">
            <input
              type="text"
              placeholder="Subject, recipients, type, body…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setActiveSearch(searchText.trim());
                  setPage(1);
                  setSearchTrigger(prev => prev + 1);
                }
              }}
            />
            <span className="email-notif-search-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', marginTop: 'auto' }}>
          <button
            className="email-notif-search-btn"
            onClick={() => { setActiveSearch(searchText.trim()); setPage(1); setSearchTrigger(prev => prev + 1); }}
          >
            Search
          </button>
          <button
            className="email-notif-clear-btn"
            onClick={() => {
              setSearchText("");
              setActiveSearch("");
              setFromDate("");
              setToDate("");
              setFilterType("");
              setPage(1);
              setSearchTrigger(prev => prev + 1);
            }}
          >
            Clear
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="email-notif-table-wrap">
        <table className="email-notif-table">
          <thead>
            <tr>
              <th style={{ width: 60 }}>ID</th>
              <th>Subject</th>
              <th>Recipients</th>
              <th>Type</th>
              <th>Status</th>
              <th>Opens</th>
              <th>Sent On</th>
              <th style={{ width: 120 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && emails.length === 0 ? (
              <tr>
                <td colSpan={8} className="email-notif-empty-state">Loading email notifications…</td>
              </tr>
            ) : emails.length > 0 ? (
              emails.map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td className="subject-cell">{row.subject}</td>
                  <td className="recipients-cell">{row.recipient_query_title || row.email || "—"}</td>
                  <td>
                    <span className="email-type-badge">
                      {row.notification_type ? row.notification_type.charAt(0).toUpperCase() + row.notification_type.slice(1) : "General"}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <span className={`email-status-badge ${row.status || "sent"}`} style={{ width: "fit-content" }}>
                        {(row.status || "sent").toUpperCase()}
                      </span>
                      {row.total_recipients > 0 && row.status !== "draft" && (
                        <div style={{ fontSize: "11px", color: "var(--color-text-light)", fontWeight: "500" }}>
                          {row.sent_count || 0} / {row.total_recipients} sent
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    {row.status !== "draft" && (row.open_count != null) ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                        <span style={{ fontWeight: 600, fontSize: "13px", color: row.open_count > 0 ? "var(--color-success, #22c55e)" : "var(--color-text-light)" }}>
                          {row.open_count || 0}{row.sent_count ? ` / ${row.sent_count}` : ""}
                        </span>
                        {row.sent_count > 0 && (
                          <span style={{ fontSize: "11px", color: "var(--color-text-light)" }}>
                            {((row.open_count || 0) / row.sent_count * 100).toFixed(1)}%
                          </span>
                        )}
                      </div>
                    ) : "—"}
                  </td>
                  <td>
                    {row.sent_at ? (
                      <>
                        <div>{formatAdminDate(row.sent_at, "—")}</div>
                        <div className="email-notif-time-sub">{formatIST(row.sent_at)?.clock || '—'}</div>
                      </>
                    ) : "—"}
                  </td>
                  <td>
                    <div className="email-notif-actions-cell">
                      {row.status === "draft" && (
                        <button
                          className="email-notif-icon-btn edit"
                          onClick={() => { setEditingDraft(row); setIsComposeOpen(true); }}
                          title="Edit Draft"
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: "18px" }}>edit</span>
                        </button>
                      )}

                      {row.status !== "draft" && (
                        <button
                          className="email-notif-icon-btn view"
                          onClick={() => setViewingEmail(row)}
                          title="View Email"
                        >
                          <EyeIcon />
                        </button>
                      )}


                      {(row.status === "pending" || row.status === "sending") && (
                        <button
                          className="email-notif-icon-btn stop"
                          onClick={() => handleCancel(row.id)}
                          title="Stop Sending"
                          style={{ color: "var(--color-danger)" }}
                        >
                          <StopIcon />
                        </button>
                      )}

                      {(() => {
                        let hasAttachments = false;
                        try {
                          const att = typeof row.attachments === 'string' ? JSON.parse(row.attachments) : (row.attachments || []);
                          hasAttachments = att.length > 0;
                        } catch (e) { }

                        return (
                          <button
                            className="email-notif-icon-btn resend"
                            onClick={() => handleResend(row.id)}
                            title={hasAttachments ? "This broadcast included attachments which are no longer available because attachments are stored temporarily. Please create a new broadcast and upload the attachments again." : "Resend"}
                            disabled={row.status === "pending" || row.status === "sending" || hasAttachments}
                            style={{ opacity: hasAttachments ? 0.5 : 1, cursor: hasAttachments ? "not-allowed" : "pointer" }}
                          >
                            <SendIcon />
                          </button>
                        );
                      })()}

                      <button
                        className="email-notif-icon-btn delete"
                        onClick={() => handleDelete(row.id)}
                        title="Delete"
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="email-notif-empty-state">No email notifications found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="email-notif-pagination">
          <div className="email-notif-pagination-info">
            Showing page {page} of {totalPages}
          </div>
          <div className="email-notif-pagination-controls">
            <button
              className="email-notif-page-btn"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              &lt;
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
              if (p === 1 || p === totalPages || (p >= page - 1 && p <= page + 1)) {
                return (
                  <button
                    key={p}
                    className={`email-notif-page-btn ${page === p ? "active" : ""}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                );
              }
              if (p === page - 2 || p === page + 2) {
                return <span key={p} className="email-notif-pagination-ellipsis">…</span>;
              }
              return null;
            })}
            <button
              className="email-notif-page-btn"
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              &gt;
            </button>
          </div>
        </div>
      )}

      {/* Compose Modal */}
      {isComposeOpen && (
        <FullscreenModal
          isOpen={isComposeOpen}
          onClose={handleModalClose}
          title="Compose Email Notification"
          maxWidth="910px"
          disableBackdropClick={true}
        >
          <ComposeEmailForm
            ref={composeRef}
            initialData={editingDraft}
            onSubmitted={() => {
              setIsComposeOpen(false);
              setEditingDraft(null);
              fetchEmails();
            }}
            onCancel={handleModalClose}
          />
        </FullscreenModal>
      )}

      {/* View Modal */}
      {viewingEmail && (
        <FullscreenModal
          isOpen={!!viewingEmail}
          onClose={() => setViewingEmail(null)}
          title="View Sent Email"
          maxWidth="700px"
          disableBackdropClick={true}
        >
          <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "20px" }}>
            <div style={{ paddingBottom: "12px", borderBottom: "1px solid var(--color-border)" }}>
              <h3 style={{ margin: "0 0 8px 0", fontSize: "18px", color: "var(--color-text-main)" }}>
                {typeof viewingEmail.subject === 'string' ? viewingEmail.subject : String(viewingEmail.subject || "")}
              </h3>
              <div style={{ display: "flex", gap: "12px", fontSize: "13px", color: "var(--color-text-light)" }}>
                <span><strong>To:</strong> {String(viewingEmail.email || viewingEmail.recipient_query_title || viewingEmail.recipient_query || "General Users")}</span>
                {Array.isArray(parseCcEmails(viewingEmail.cc_emails)) && parseCcEmails(viewingEmail.cc_emails).length > 0 && (
                  <span><strong>CC:</strong> {parseCcEmails(viewingEmail.cc_emails).map(c => typeof c === 'string' ? c : (c?.email || "")).filter(Boolean).join(", ")}</span>
                )}
                <span><strong>Date:</strong> {formatAdminDateTime(viewingEmail.created_at, "—")}</span>
              </div>
            </div>

            <div
              style={{ padding: "16px", backgroundColor: "#fff", border: "1px solid var(--color-border)", borderRadius: "8px", minHeight: "250px", overflowX: "auto" }}
              dangerouslySetInnerHTML={{ __html: typeof viewingEmail.html_body === 'string' ? viewingEmail.html_body : "" }}
            />
          </div>
        </FullscreenModal>
      )}

      {/* Email Open Events Modal */}
      {opensModal && (
        <FullscreenModal
          isOpen={!!opensModal}
          onClose={() => { setOpensModal(null); setOpensData(null); }}
          title="Email Open Tracking"
          maxWidth="820px"
          disableBackdropClick={false}
        >
          <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ paddingBottom: "12px", borderBottom: "1px solid var(--color-border)" }}>
              <h3 style={{ margin: "0 0 8px 0", fontSize: "16px", color: "var(--color-text-main)" }}>
                {opensModal.subject}
              </h3>
              <div style={{ display: "flex", gap: "16px", fontSize: "13px", color: "var(--color-text-light)", flexWrap: "wrap" }}>
                <span><strong>Sent:</strong> {opensModal.sent_count || 0}</span>
                {opensData && (
                  <>
                    <span style={{ color: "var(--color-success, #22c55e)", fontWeight: 600 }}>Opened: {opensData.uniqueOpened || 0}</span>
                    <span>Not Opened: {opensData.neverOpened || 0}</span>
                    <span style={{ fontWeight: 600 }}>Open Rate: {opensData.openRate || 0}%</span>
                  </>
                )}
              </div>
              <div style={{ marginTop: "8px", fontSize: "11px", color: "var(--color-text-light)", fontStyle: "italic" }}>
                Note: "Opened" means the tracking pixel was loaded by the email client. This is not a guarantee the recipient read the email.
              </div>
            </div>

            {opensLoading ? (
              <div style={{ textAlign: "center", padding: "40px 0", color: "var(--color-text-light)" }}>Loading tracking data…</div>
            ) : opensData && opensData.recipients && opensData.recipients.length > 0 ? (
              <>
                <div className="email-notif-table-wrap">
                  <table className="email-notif-table">
                    <thead>
                      <tr>
                        <th>Recipient</th>
                        <th>Status</th>
                        <th>First Opened</th>
                        <th>Last Opened</th>
                        <th>Opens</th>
                      </tr>
                    </thead>
                    <tbody>
                      {opensData.recipients.map((r, idx) => (
                        <tr key={idx}>
                          <td style={{ fontSize: "13px" }}>{r.email}</td>
                          <td>
                            <span className={`email-status-badge ${r.opened ? "sent" : "failed"}`} style={{ width: "fit-content", fontSize: "11px" }}>
                              {r.opened ? "Opened" : "Not Opened"}
                            </span>
                          </td>
                          <td style={{ fontSize: "12px", color: "var(--color-text-light)" }}>
                            {r.firstOpenedAt ? formatAdminDateTime(r.firstOpenedAt, "—") : "—"}
                          </td>
                          <td style={{ fontSize: "12px", color: "var(--color-text-light)" }}>
                            {r.lastOpenedAt ? formatAdminDateTime(r.lastOpenedAt, "—") : "—"}
                          </td>
                          <td style={{ fontWeight: 600 }}>{r.openCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {opensData.recipients.length > 0 && opensData.totalRecipients > 20 && (
                  <div className="email-notif-pagination">
                    <div className="email-notif-pagination-info">
                      Showing page {opensPage} of {Math.ceil(opensData.totalRecipients / 20)}
                    </div>
                    <div className="email-notif-pagination-controls">
                      <button className="email-notif-page-btn" disabled={opensPage === 1} onClick={() => handleOpensPageChange(opensPage - 1)}>&lt;</button>
                      <span style={{ padding: "0 8px", fontSize: "13px" }}>{opensPage}</span>
                      <button className="email-notif-page-btn" disabled={opensPage >= Math.ceil(opensData.totalRecipients / 20)} onClick={() => handleOpensPageChange(opensPage + 1)}>&gt;</button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "40px 0", color: "var(--color-text-light)" }}>
                No tracking data available for this email.
              </div>
            )}
          </div>
        </FullscreenModal>
      )}
    </div>
  );
};

export default EmailNotification;
